// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo, useRef } from 'react';

import type { ElementId, MachineElement, StrongExtract } from '@evolvemep/api-ts';

import type { ALL_MODULE_ROUTES } from 'app/ModuleRoutes';
import { useRouteSearchParam } from 'app/useRouteSearchParam';
import { isNotNil } from 'helpers/isNotNil';

import { useMachineElementVariableTrackingModals } from './modalRegistry';
import type { FabricationWorkType } from './useDoUndoMachineElement';

/** Called after the bend has been changed from inside the Bend QA modal. */
type OnBendUpdated = (updatedElement: MachineElement) => void;

/** The routes that carry a `bendId` search param, and can therefore host the Bend QA modal. */
export type BendQARoute = StrongExtract<keyof typeof ALL_MODULE_ROUTES, '/fabrication' | '/work-orders/:workOrderId'>;

type Opts = {
  /** The route this provider is mounted on. Its `bendId` search param drives the modal. */
  route: BendQARoute;
  /** A callback to mark a bend as done. Used in an effect, must be memoized. */
  onMarkBendDone: (
    machineElement: MachineElement,
    workType: FabricationWorkType,
    onBendUpdated: OnBendUpdated,
  ) => Promise<void>;
};

/**
 * Drives the Bend QA modal from the `bendId` search param: whenever one is present, the modal is open on that bend.
 *
 * Keeping the URL as the only source of truth means a bend survives a full page load — notably the round trip
 * through Autodesk authentication, which navigates away and then restores the previous location.
 *
 * The modal itself is opened through the machine element modal registry, so it stacks with the rest of the
 * fabrication modals instead of managing its own layering.
 *
 * @remarks Do not call directly. Instead, access through context with {@link useMachineElementActions}.
 */
export const useBendQA = ({ route, onMarkBendDone: parentOnMarkBendDone }: Opts) => {
  const [bendId, setBendId] = useRouteSearchParam(route, 'bendId');
  const onBendUpdatedRef = useRef<((updatedBend: MachineElement) => void) | undefined>(undefined);

  /**
   * Opens the Bend QA modal for the given bend by writing `bendId` to the URL.
   *
   * @param onBendUpdated Called each time the bend changes while the modal is open. Not persisted across a page
   * reload, so a bend opened from a pasted or restored URL reports no updates.
   */
  const viewBend = useCallback(
    (elementId: ElementId, onMarkAsDone?: OnBendUpdated) => {
      onBendUpdatedRef.current = onMarkAsDone;
      setBendId(elementId);
    },
    [setBendId],
  );

  const onClose = useCallback(() => {
    setBendId(null);
    onBendUpdatedRef.current = undefined;
  }, [setBendId]);

  const onBendUpdated = useCallback((updatedBend: MachineElement) => {
    onBendUpdatedRef.current?.(updatedBend);
  }, []);

  const onMarkBendDone = useCallback(
    async (bend: MachineElement) => {
      await parentOnMarkBendDone(bend, 'Bend', (updatedBend) => {
        onBendUpdatedRef.current?.(updatedBend);
        onClose();
      });
    },
    [onClose, parentOnMarkBendDone],
  );

  useMachineElementVariableTrackingModals('bendQaModal', {
    variable: bendId,
    props: (trackedBendId) => ({ bendId: trackedBendId, onMarkBendDone, onBendUpdated }),
    onClose,
  });

  return useMemo(
    () => ({
      viewBend,
      bendQAOpened: isNotNil(bendId),
    }),
    [viewBend, bendId],
  );
};
